"use client";

import ModalWindows from "@/components/ModalWindows";
import { DesktopNavItem } from "@/components/navbar/DesktopNavItem";
import { Button } from "@/components/ui/Button";
import { useAdminContext } from "@/context/AdminContext";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { useSessionContext } from "@/context/SessionContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import { Popover, PopoverGroup } from "@headlessui/react";
import {
  ArrowUpCircleIcon,
  Bars3Icon,
  ChevronRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  LockClosedIcon,
  PhotoIcon,
} from "@heroicons/react/24/solid";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

const DesktopNav = () => {
  const {
    isLoggedIn,
    handleLogout,
    setMobileMenuOpen,
    PRODUCTS,
    NAVBAR_ITEMS,
    PROFILE_ITEMS,
    blog,
    userInfoGlobal,
  } = useAppContext();
  const { LOGGED_IN_ITEMS, USER_ESTATE_ITEMS, setIsOpen, setSelectedItem } =
    useMenuItemsContext();
  const { isInitialLoading } = useSessionContext();
  const { isDisabled } = useWatchDogContext();
  const { ADMIN_ITEMS, isAdmin } = useAdminContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      changeMembership: "Změnit členství",
      membership: "Členství",
      demo: "Ukázka",
      compareMemberships: "Porovnat členství",
      upgrade: "Zvýšit členství",
      administration: "Administrace",
      watchdog: "Hlídací pes",
      portfolio: "Mé portfolio",
      profile: "Profil",
      logout: "Odhlásit se",
    },
    en: {
      changeMembership: "Change membership",
      membership: "Membership",
      demo: "Demo",
      compareMemberships: "Compare memberships",
      upgrade: "Upgrade now",
      administration: "Administration",
      watchdog: "WatchDog",
      portfolio: "Portfolio",
      profile: "Profile",
      logout: "Log out",
    },
  }[language];
  const isBasicClient = userInfoGlobal?.membership === "BASIC";
  const pathname = usePathname();
  const isNewPage = pathname === "/";
  const guestTextLinks = NAVBAR_ITEMS.filter((item) => {
    if (item.href === "/blog") return false;
    if (item.href === "/#prihlaseni") return false;
    if (isNewPage) {
      if (item.href === "/#ukazka") return false;
      return [
        "/#produkty",
        "/portfolio",
        "/hlidaci-pes",
        "/#cenik",
        "/#faq",
      ].includes(item.href);
    }
    return true;
  }).map((item) => {
    if (!isNewPage) return item;
    if (item.href === "/#o-psovi") return { ...item, href: "#o-psovi" };
    if (item.href === "/#cenik") return { ...item, href: "#cenik" };
    if (item.href === "/#recenze") return { ...item, href: "#recenze" };
    return item;
  });
  const productItems = isNewPage
    ? PRODUCTS.map((item) => ({ ...item, href: "#cenik" }))
    : PRODUCTS;
  const openUpgradeModal = () => {
    setSelectedItem({
      name: "Změnit členství",
      href: "",
      icon: ArrowUpCircleIcon,
    });
    setIsOpen(true);
  };
  const { isDark } = useAppContext();

  return (
    <nav aria-label="Global" className="desktop-nav">
      {/* Logo */}
      <div className="desktop-nav__logo-wrapper">
        <Link href="/" className="desktop-nav__logo-link">
          <span className="sr-only">SOFTIQ</span>
          <Image
            height={0}
            width={0}
            alt="SOFTIQ - hlídací pes na nemovitosti"
            priority
            src={
              isDark
                ? "/image/logos/SOFTIQ-hlidaci-pes-dark.svg"
                : "/image/logos/SOFTIQ-hlidaci-pes.svg"
            }
            className="desktop-nav__logo-img"
          />
        </Link>
      </div>

      {/* Mobile menu button */}
      <Button
        type="button"
        variant="unstyled"
        aria-label="Open menu"
        className="desktop-nav__mobile-toggle text-foreground lg:!hidden"
        onClick={() => setMobileMenuOpen(true)}
      >
        <Bars3Icon
          aria-hidden="true"
          className="desktop-nav__mobile-toggle-icon size-7"
        />
      </Button>

      {/* Not logged in */}
      {!isLoggedIn && (
        <div className="desktop-nav__guest-group">
          <PopoverGroup className="desktop-nav__popover-group">
            {/* Členství */}
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button className="desktop-nav__popover-btn">
                    {copy.membership}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="desktop-nav__chevron"
                    />
                  </Popover.Button>

                  <Popover.Overlay className="desktop-nav__overlay" />

                  <Popover.Panel className="desktop-nav__panel--wide">
                    <div className="desktop-nav__panel-inner">
                      {productItems.map((item) => (
                        <div
                          key={item.name}
                          className="desktop-nav__product-item group"
                          onClick={() => close()}
                        >
                          <div className="desktop-nav__product-icon-wrapper">
                            <item.icon
                              aria-hidden="true"
                              className="desktop-nav__product-icon"
                            />
                          </div>
                          <div className="desktop-nav__product-body">
                            <Link
                              href={item.href}
                              className="desktop-nav__product-link"
                            >
                              {item.name}
                              <span className="absolute inset-0" />
                            </Link>
                            <p className="desktop-nav__product-description">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className={
                        isNewPage
                          ? "desktop-nav__cta-grid desktop-nav__cta-grid--single"
                          : "desktop-nav__cta-grid"
                      }
                    >
                      {!isNewPage && (
                        <Link
                          href="/#ukazka"
                          className="desktop-nav__cta-link"
                          onClick={() => close()}
                        >
                          <PhotoIcon
                            aria-hidden="true"
                            className="desktop-nav__cta-icon"
                          />
                          {copy.demo}
                        </Link>
                      )}
                      <Link
                        href={isNewPage ? "#cenik" : "/#cenik"}
                        className="desktop-nav__cta-link"
                        onClick={() => close()}
                      >
                        <PlusIcon
                          aria-hidden="true"
                          className="desktop-nav__cta-icon"
                        />
                        {copy.compareMemberships}
                      </Link>
                    </div>
                  </Popover.Panel>
                </>
              )}
            </Popover>

            {guestTextLinks.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className="desktop-nav__text-link"
              >
                {item.name}
              </Link>
            ))}
          </PopoverGroup>
        </div>
      )}

      {/* Logged in */}
      <PopoverGroup className="desktop-nav__logged-in-group">
        {isLoggedIn && !isInitialLoading && (
          <>
            {isBasicClient && (
              <Button
                type="button"
                className="desktop-nav__text-link"
                onClick={openUpgradeModal}
              >
                <ArrowUpCircleIcon aria-hidden="true" className="size-4" />
                {copy.upgrade}
              </Button>
            )}
            {/* Administrace */}
            {isAdmin && (
              <Popover className="relative">
                {({ close }) => (
                  <>
                    <Popover.Button className="desktop-nav__popover-btn">
                      {copy.administration}
                      <ChevronDownIcon
                        aria-hidden="true"
                        className="desktop-nav__chevron"
                      />
                    </Popover.Button>
                    <Popover.Overlay className="desktop-nav__overlay" />

                    <Popover.Panel className="desktop-nav__panel--narrow">
                      <div className="desktop-nav__panel-inner">
                        {ADMIN_ITEMS.map((item, idx) => (
                          <DesktopNavItem
                            item={item}
                            key={idx}
                            onClose={close}
                          />
                        ))}
                      </div>
                    </Popover.Panel>
                  </>
                )}
              </Popover>
            )}

            {/* Hlídací pes */}
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button className="desktop-nav__popover-btn">
                    {copy.watchdog}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="desktop-nav__chevron"
                    />
                  </Popover.Button>
                  <Popover.Overlay className="desktop-nav__overlay" />

                  <Popover.Panel className="desktop-nav__panel--narrow">
                    <div className="desktop-nav__panel-inner">
                      {LOGGED_IN_ITEMS.map((item, idx) => {
                        const isFirstItemDisabled = idx === 0 && isDisabled;
                        if (isFirstItemDisabled) return;
                        return (
                          <DesktopNavItem
                            item={item}
                            key={idx}
                            onClose={close}
                          />
                        );
                      })}
                    </div>
                  </Popover.Panel>
                </>
              )}
            </Popover>

            {/* Mé portfolio */}
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button className="desktop-nav__popover-btn">
                    {copy.portfolio}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="desktop-nav__chevron"
                    />
                  </Popover.Button>
                  <Popover.Overlay className="desktop-nav__overlay" />

                  <Popover.Panel className="desktop-nav__panel--fixed">
                    <div className="desktop-nav__panel-inner">
                      {USER_ESTATE_ITEMS.map((item, idx) => (
                        <DesktopNavItem item={item} key={idx} onClose={close} />
                      ))}
                    </div>
                  </Popover.Panel>
                </>
              )}
            </Popover>

            {/* Profil */}
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button className="desktop-nav__popover-btn">
                    {copy.profile}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="desktop-nav__chevron"
                    />
                  </Popover.Button>
                  <Popover.Overlay className="desktop-nav__overlay" />

                  <Popover.Panel className="desktop-nav__panel--centered">
                    <div className="desktop-nav__panel-inner pb-0">
                      {PROFILE_ITEMS.map((item, idx) => (
                        <DesktopNavItem item={item} key={idx} onClose={close} />
                      ))}
                    </div>

                    <div className="desktop-nav__logout-section">
                      <div
                        onClick={() => {
                          handleLogout();
                          close();
                        }}
                        className="group settings-row cursor-pointer"
                      >
                        <div className="settings-icon-wrapper">
                          <LockClosedIcon
                            aria-hidden="true"
                            className="settings-icon"
                          />
                        </div>
                        <div className="flex-auto min-w-0">
                          <span className="settings-label truncate">
                            {copy.logout}
                            <span className="absolute inset-0" />
                          </span>
                        </div>
                        <ChevronRightIcon className="settings-chevron-shift" />
                      </div>
                    </div>
                  </Popover.Panel>
                </>
              )}
            </Popover>
          </>
        )}
      </PopoverGroup>
      <ModalWindows />
    </nav>
  );
};

export default DesktopNav;
